import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Switch, Route, withRouter, Redirect } from 'react-router';
import { Link } from 'react-router-dom';
import { TransitionGroup, CSSTransition } from 'react-transition-group';
import { toast } from 'react-toastify';

import { logoutUser, buildAuthRoleState, isPortalPathAllowed, getPortalDefaultPath, canAccessProductsMenu, canAccessWorkPeriodsMenu, canAccessUsersMenu } from '../../actions/auth';
import { getProductsRequest } from '../../actions/products';
import { loadProductCatalogIfNeeded } from '../../actions/productCatalog';
import { getMerchantId } from '../../utils/merchant';
import { getUsersRequest } from '../../actions/users';
import { getWorkperiodsRequest } from '../../actions/sales';
import { loadStoreDetails } from '../../actions/settings';

import Products from '../../pages/products/Products';
import Settings from '../../pages/settings/Settings';
import Users from '../../pages/users';
import Sales from '../../pages/sales';

import logo_img from '../../images/logo.png';
import { bindHeaderOffsetSync, syncHeaderOffset } from '../../utils/headerLayout';
import { withTranslation } from '../../i18n/LanguageContext';
import LanguageSelect from '../../i18n/LanguageSelect';
import { helpManualUrl } from '../../i18n/helpUrl';

class Layout extends React.Component {
  static propTypes = {
    activeItem: PropTypes.string,
    dispatch: PropTypes.func.isRequired,
  };

  static defaultProps = {
    activeItem: 'products'
  };

  state = {
    isNavMenu: false
  };

  constructor(props) {
    super(props);
    this.doLogout = this.doLogout.bind(this);
    this.headerRef = React.createRef();
    this.unbindHeaderOffsetSync = null;
  }

  componentDidMount() {
    this.enforcePortalRouteAccess();
    this.ensureProductCatalogLoaded();
    if (this.headerRef.current) {
      this.unbindHeaderOffsetSync = bindHeaderOffsetSync(this.headerRef.current);
    }
  }

  componentWillUnmount() {
    if (this.unbindHeaderOffsetSync) {
      this.unbindHeaderOffsetSync();
      this.unbindHeaderOffsetSync = null;
    }
  }

  componentDidUpdate(prevProps) {
    const rolesJustLoaded = !prevProps.identityRolesLoaded && this.props.identityRolesLoaded;
    const pathChanged = prevProps.location.pathname !== this.props.location.pathname;
    const merchantChanged = getMerchantId(prevProps.merchant) !== getMerchantId(this.props.merchant);

    if (rolesJustLoaded || pathChanged) {
      this.enforcePortalRouteAccess();
    }

    if (merchantChanged || pathChanged) {
      this.ensureProductCatalogLoaded();
    }
  }

  ensureProductCatalogLoaded() {
    if (this.props.location.pathname.startsWith('/admin/products')) {
      this.props.dispatch(loadProductCatalogIfNeeded());
    }
  }

  enforcePortalRouteAccess() {
    const { identityRolesLoaded, location, history } = this.props;
    if (!identityRolesLoaded) {
      return;
    }

    const authRoleState = buildAuthRoleState(this.props.auth);
    const { pathname } = location;

    if (!isPortalPathAllowed(pathname, authRoleState)) {
      history.replace(getPortalDefaultPath(authRoleState));
    }
  }

  toggleNavMenu() {
    this.setState(prev => ({ isNavMenu: !prev.isNavMenu }));
  }

  closeNavMenu() {
    this.setState({ isNavMenu: false });
  }

  doLogout(e) {
    e.preventDefault();
    this.props.dispatch(logoutUser());
    window.location.href="/";
  }

  handleHeaderImageLoad = () => {
    if (this.headerRef.current) {
      syncHeaderOffset(this.headerRef.current);
    }
  }

  handleRefresh = () => {
    const { pathname } = this.props.location;
    const merchantId = getMerchantId(this.props.merchant);

    if ((pathname.startsWith('/admin/products') || pathname.startsWith('/admin/users')) && !merchantId) {
      toast.error(this.props.t('header.merchantUnavailable'));
      return;
    }

    if (pathname.startsWith('/admin/products')) {
      this.props.dispatch(getProductsRequest(merchantId));
    } else if (pathname.startsWith('/admin/sales')) {
      this.props.dispatch(getWorkperiodsRequest());
    } else if (pathname.startsWith('/admin/users')) {
      this.props.dispatch(getUsersRequest(merchantId));
    } else if (pathname.startsWith('/admin/settings')) {
      this.props.dispatch(loadStoreDetails());
    }

    toast.success(this.props.t('header.refreshToast'));
  }

  render() {
    const { pathname } = this.props.location;
    const { isNavMenu } = this.state;
    const { t } = this.props;
    const authRoleState = buildAuthRoleState(this.props.auth);
    const defaultPath = getPortalDefaultPath(authRoleState);
    const logoPath = authRoleState.portalLogoPath || defaultPath;

    const navItems = [
      canAccessProductsMenu(authRoleState)
        ? { path: '/admin/products', icon: 'zmdi-cutlery', label: t('nav.products') }
        : null,
      canAccessWorkPeriodsMenu(authRoleState)
        ? { path: '/admin/sales', icon: 'zmdi-chart', label: t('nav.workPeriods') }
        : null,
      canAccessUsersMenu(authRoleState)
        ? { path: '/admin/users', icon: 'zmdi-accounts-alt', label: t('nav.users') }
        : null,
      { path: authRoleState.canAccessFullSettingsTabs ? '/admin/settings' : '/admin/settings/payout', icon: 'zmdi-settings', label: t('nav.settings') },
    ].filter(Boolean);

    return (
      <div className="app-layout">
        <header className="app-header" ref={this.headerRef}>
          <div className="app-header__inner">
            <Link to={logoPath} className="app-header__logo">
              <img src={logo_img} alt={t('nav.logoAlt')} onLoad={this.handleHeaderImageLoad} />
            </Link>

            <div className="app-header__tools">
              <LanguageSelect id="header-display-language" />
              <button
                className={`app-header__refresh${this.props.isRefreshing ? ' app-header__refresh--spinning' : ''}`}
                onClick={this.handleRefresh}
                aria-label={t('common.refresh')}
                disabled={this.props.isRefreshing}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="23 4 23 10 17 10"></polyline>
                  <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"></path>
                </svg>
              </button>
            </div>

            <button
              className="app-header__toggle"
              onClick={() => this.toggleNavMenu()}
              aria-label={t('nav.toggle')}
            >
              {isNavMenu ? (
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
              ) : (
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                  <line x1="3" y1="6" x2="21" y2="6"></line>
                  <line x1="3" y1="12" x2="21" y2="12"></line>
                  <line x1="3" y1="18" x2="21" y2="18"></line>
                </svg>
              )}
            </button>
          </div>
        </header>

        <div className="app-header-spacer" aria-hidden="true" />

        {isNavMenu && <div className="app-nav-overlay app-nav-overlay--visible" onClick={() => this.closeNavMenu()}></div>}
        <nav className={`app-header__nav ${isNavMenu ? 'app-header__nav--open' : ''}`}>
          {navItems.map(item => (
            <Link
              key={item.path}
              to={item.path}
              className={`app-header__nav-item ${(item.exact ? pathname === item.path : pathname.startsWith(item.path)) ? 'app-header__nav-item--active' : ''}`}
              onClick={() => this.closeNavMenu()}
            >
              <i className={`zmdi ${item.icon}`}></i>
              <span>{item.label}</span>
            </Link>
          ))}
          <button className="app-header__nav-item app-header__nav-item--help" onClick={() => window.open(helpManualUrl(this.props.language), '_blank')}>
            <i className="zmdi zmdi-help-outline"></i>
            <span>{t('nav.help')}</span>
          </button>
          <button className="app-header__nav-item app-header__nav-item--logout" onClick={this.doLogout}>
            <i className="zmdi zmdi-power"></i>
            <span>{t('nav.logOut')}</span>
          </button>
        </nav>

        <main className="app-main">
          <TransitionGroup>
            <CSSTransition
              key={this.props.location.key}
              classNames="fade"
              timeout={200}
            >
              <Switch>
                <Route path="/admin" exact render={() => <Redirect to={defaultPath} />} />
                <Route
                  path="/admin/products"
                  render={() => (
                    canAccessProductsMenu(authRoleState)
                      ? <Products />
                      : <Redirect to={defaultPath} />
                  )}
                />
                <Route
                  path="/admin/sales"
                  render={() => (
                    canAccessWorkPeriodsMenu(authRoleState)
                      ? <Sales />
                      : <Redirect to={defaultPath} />
                  )}
                />
                <Route path="/admin/settings" component={Settings} />
                <Route
                  path="/admin/users"
                  render={() => (
                    canAccessUsersMenu(authRoleState)
                      ? <Users />
                      : <Redirect to={defaultPath} />
                  )}
                />
              </Switch>
            </CSSTransition>
          </TransitionGroup>
        </main>
      </div>
    );
  }
}

function mapStateToProps(store) {
  return {
    activeItem: store.navigation.activeItem,
    merchant: store.auth.merchant,
    auth: store.auth,
    identityRolesLoaded: store.auth.identityRolesLoaded || false,
    isRefreshing: store.products.isReceiving || store.users.isReceiving || store.sales.workperiodsLoading || store.settings.isReceiving,
  };
}

export default withRouter(connect(mapStateToProps)(withTranslation(Layout)));
